import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperclip, faPaperPlane, faTimes, faXmark,
} from '@fortawesome/free-solid-svg-icons';
import {
  AttachButton,
  ComposerHint,
  ComposerRow,
  ComposerTextarea,
  ConversationModalBody,
  ConversationModalCard,
  ConversationModalFooter,
  ConversationModalHeader,
  ConversationModalOverlay,
  DropOverlay,
  EmptyConversationState,
  LoadingThreadState,
  MessageAttachmentChip,
  MessageBubble,
  MessageBubbleRow,
  PendingAttachmentChip,
  PendingAttachmentsList,
  SendButton,
} from './ConversationModal.styles';
import managementAxe from '../../../../services/axios-client/managementServisAxeClient';
import { generalAlertErrorToast } from '../../../utils/alerts';
import { apiDownloadThreadMessageAttachment } from '../../../../services/axios-client/axeCommons';

const MAX_ATTACHMENTS = 5;
const ACCEPTED_FILES = '.doc,.docx,application/msword,text/plain,application/pdf,image/png,image/jpeg,image/jpg';

const senderLabel = (message) => {
  if (message.broker_agent) {
    return `${message.broker_agent.name} ${message.broker_agent.surname}`.trim();
  }
  if (message.user) {
    return message.user.name;
  }
  return 'Tu';
};

/**
 * Generic conversation panel: lists the messages of the current thread (if
 * any) and lets the user either start a new thread or reply to the existing
 * one, with optional attachments. Meant to be a drop-in replacement for the
 * legacy SupportModal/MessagesModal pair, rolled out product by product.
 *
 * Only `threadId` (and, for a brand new conversation, `requestId`) is
 * required from the parent — the full thread (with its messages) is fetched
 * here, so callers only ever need to know the id, not the message list.
 */
function ConversationModal({
  title = 'Contatta il Backoffice',
  requestId,
  threadId,
  location = 'threads_page',
  onClose,
  onUpdated = () => { },
}) {
  const [activeThread, setActiveThread] = useState(null);
  const [isLoadingThread, setIsLoadingThread] = useState(!!threadId);
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [localMessages, setLocalMessages] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [isNudging, setIsNudging] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const bodyRef = useRef(null);

  const nudgeModal = () => {
    setIsNudging(true);
    setTimeout(() => setIsNudging(false), 180);
  };

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, []);

  const fetchThread = async (id) => {
    try {
      const res = await managementAxe.get(`/broker/messages/get-thread-broker/${id}`);
      setActiveThread(res.data.data);
      setLocalMessages([]);
    } catch (error) {
      generalAlertErrorToast('Non è stato possibile caricare la conversazione');
    } finally {
      setIsLoadingThread(false);
    }
  };

  useEffect(() => {
    if (threadId) {
      setIsLoadingThread(true);
      fetchThread(threadId);
    } else {
      setActiveThread(null);
      setIsLoadingThread(false);
    }
  }, [threadId]);

  const messages = [...(activeThread?.messages || []), ...localMessages];

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages.length]);

  const handleAttachmentFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    if (attachments.length + files.length > MAX_ATTACHMENTS) {
      generalAlertErrorToast(`Puoi allegare al massimo ${MAX_ATTACHMENTS} file`);
      return;
    }

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachments((prev) => [...prev, {
          original_filename: file.name,
          content: reader.result.replace(`data:${file.type};base64,`, ''),
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePaste = (e) => {
    const pasted = Array.from(e.clipboardData?.files || []);
    const images = pasted
      .filter((file) => file.type.startsWith('image/'))
      .map((file) => (file.name && file.name !== 'image.png'
        ? file
        : new File([file], `screenshot-${Date.now()}.${file.type.split('/')[1] || 'png'}`, { type: file.type })));

    if (images.length > 0) {
      e.preventDefault();
      handleAttachmentFiles(images);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleAttachmentFiles(e.dataTransfer.files);
  };

  const sendMessage = async () => {
    if (message.trim() === '') {
      generalAlertErrorToast('Scrivi un messaggio prima di inviare');
      return;
    }

    setIsSending(true);

    const payload = {
      body: message,
      attachments,
    };
    const request = activeThread
      ? managementAxe.put(`/broker/messages/reply-thread-broker/${activeThread.id}`, {
        subject: payload.body,
        attachments,
      })
      : managementAxe.post('/broker/messages/create-thread-broker', { ...payload, request_id: requestId, location });

    try {
      const res = await request;
      if (res.status === 200) {
        setLocalMessages((prev) => [...prev, {
          id: `local-${Date.now()}`,
          body: message,
          attachments,
          created_at: new Date().toISOString(),
          user: null,
          broker_agent: { name: 'Tu', surname: '' },
        }]);
        setMessage('');
        setAttachments([]);
        onUpdated();

        if (activeThread) {
          fetchThread(activeThread.id);
        }
      }
    } catch (error) {
      generalAlertErrorToast('Non è stato possibile inviare il messaggio, riprova');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isSending) sendMessage();
    }
  };

  const downloadAttachment = async (attachmentId) => {
    const response = await apiDownloadThreadMessageAttachment(attachmentId);
    const alink = document.createElement('a');
    alink.target = '_blank';
    alink.href = response.data.data.content;
    alink.download = response.data.data.filename;
    alink.click();
  };

  return createPortal(
    <ConversationModalOverlay
      onClick={nudgeModal}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => e.preventDefault()}
    >
      <ConversationModalCard
        $nudge={isNudging}
        onClick={(e) => e.stopPropagation()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragging && (
          <DropOverlay>
            <FontAwesomeIcon icon={faPaperclip} />
            Rilascia i file qui
          </DropOverlay>
        )}
        <ConversationModalHeader>
          <h5>{activeThread ? (activeThread.subject || title) : title}</h5>
          <button type="button" onClick={onClose} aria-label="Chiudi">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </ConversationModalHeader>

        <ConversationModalBody ref={bodyRef}>
          {isLoadingThread ? (
            <LoadingThreadState>
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Caricamento...</span>
              </div>
              Caricamento conversazione...
            </LoadingThreadState>
          ) : messages.length === 0 ? (
            <EmptyConversationState>
              Nessun messaggio, scrivi qui sotto per contattare il backoffice.
            </EmptyConversationState>
          ) : (
            messages.map((msg, index) => {
              const isOwn = msg.user_id === undefined ? true : msg.user_id === null;
              return (
                <MessageBubbleRow key={msg.id ?? index} $isOwn={isOwn}>
                  <MessageBubble $isOwn={isOwn}>
                    <div className="meta">
                      <span className="sender">{senderLabel(msg)}</span>
                      <span className="timestamp">{moment(msg.created_at).fromNow()}</span>
                    </div>
                    {msg.body && <div className="body">{msg.body}</div>}
                    {(msg.attachments || []).map((file, fileIndex) => (
                      <MessageAttachmentChip key={`${msg.id}-${fileIndex}`} onClick={() => downloadAttachment(file.id)}>
                        <FontAwesomeIcon icon={faPaperclip} />
                        <span>{file.original_filename}</span>
                      </MessageAttachmentChip>
                    ))}
                  </MessageBubble>
                </MessageBubbleRow>
              );
            })
          )}
        </ConversationModalBody>

        <ConversationModalFooter>
          {attachments.length > 0 && (
            <PendingAttachmentsList>
              {attachments.map((file, index) => (
                <PendingAttachmentChip key={`${file.original_filename}-${index}`}>
                  <span>{file.original_filename}</span>
                  <button type="button" onClick={() => removeAttachment(index)} aria-label="Rimuovi allegato">
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </PendingAttachmentChip>
              ))}
            </PendingAttachmentsList>
          )}

          <ComposerRow>
            <AttachButton htmlFor="conversation-modal-attachments">
              <FontAwesomeIcon icon={faPaperclip} />
              <input
                id="conversation-modal-attachments"
                type="file"
                hidden
                multiple
                accept={ACCEPTED_FILES}
                onChange={(e) => {
                  handleAttachmentFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </AttachButton>

            <ComposerTextarea
              rows={1}
              placeholder="Scrivi un messaggio..."
              value={message}
              disabled={isSending}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
            />

            <SendButton type="button" onClick={sendMessage} disabled={isSending}>
              <FontAwesomeIcon icon={faPaperPlane} />
            </SendButton>
          </ComposerRow>

          <ComposerHint>
            <FontAwesomeIcon icon={faPaperclip} />
            Allega un file con la graffetta, incolla uno screenshot con Ctrl/Cmd+V o trascina i file qui.
          </ComposerHint>
        </ConversationModalFooter>
      </ConversationModalCard>
    </ConversationModalOverlay>,
    document.body,
  );
}

export default ConversationModal;
